import PageHeader from '../components/ui/PageHeader';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArticleService } from '../services/articleService';
import { Article } from '../types';
import { formatScheduledAtGerman } from '../utils/dateFormatter';
import { getScheduledAtTime } from '../utils/dateTimeUtils';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import PageLoader from '../components/ui/PageLoader';
import { RescheduleModal } from '../components/RescheduleModal';
import ArticleCard from '../components/ArticleCard';
import useArticleLiveUpdates from '../hooks/useArticleLiveUpdates';
import { groupArticlesByStatus } from '../utils/articleStatus';
import { 
  DocumentTextIcon, 
  PlusIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

export const ArticleListPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [loadError, setLoadError] = useState('');
  const [articles, setArticles] = useState<Article[]>([]);
  useArticleLiveUpdates(user, setArticles);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const articlesPerPage = 10;
  
  // Reschedule Modal State
  const [rescheduleArticle, setRescheduleArticle] = useState<Article | null>(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const isEditorOrAdmin = hasRole(['administrator', 'redakteur']);

  useEffect(() => {
    loadArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasRole]);

  const loadArticles = async (page = 0) => {
    if (!user) return;

    try {
      setIsLoading(page === 0);
      setIsLoadingMore(page > 0);

      // Alle Benutzer sehen nur ihre eigenen Artikel
      const userArticles = await ArticleService.getUserArticles(user.id, user);

      setLoadError('');
      const sortedArticles = groupArticlesByStatus(userArticles).flatMap(group =>
        group.articles.sort((a, b) => {
          if (group.id === 'published') return (b.publishedAt?.getTime() || b.updatedAt.getTime()) - (a.publishedAt?.getTime() || a.updatedAt.getTime());
          return (getScheduledAtTime(a.scheduledAt) ?? Number.MAX_SAFE_INTEGER) - (getScheduledAtTime(b.scheduledAt) ?? Number.MAX_SAFE_INTEGER);
        })
      );

      // Paginierung
      const startIndex = page * articlesPerPage;
      const endIndex = startIndex + articlesPerPage;
      const paginatedArticles = sortedArticles.slice(startIndex, endIndex);

      if (page === 0) {
        setArticles(paginatedArticles);
      } else {
        setArticles(prev => [...prev, ...paginatedArticles]);
      }

      setHasMore(endIndex < sortedArticles.length);
      setCurrentPage(page);
    } catch (error) {
      setLoadError('Die Artikel konnten nicht geladen werden. Bitte versuche es erneut.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!isLoadingMore && hasMore) {
      loadArticles(currentPage + 1);
    }
  };

  // Reschedule Modal Handler
  const openRescheduleModal = (article: Article) => {
    setRescheduleArticle(article);
    setShowRescheduleModal(true);
  };

  const closeRescheduleModal = () => {
    setShowRescheduleModal(false);
    setRescheduleArticle(null);
  };

  const handleRescheduleSuccess = () => {
    // Artikel neu laden nach erfolgreichem Umplanen
    loadArticles(0);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <PageHeader title={<>Meine Artikel</>} description={<>Entwürfe, Einreichungen und bestätigte Veröffentlichungen</>} />
        </div>
        <Link to="/wizard">
          <Button className="flex items-center">
            <PlusIcon className="w-4 h-4 mr-2" />
            Neuer Artikel
          </Button>
        </Link>
      </div>

      {loadError && <div role="alert" className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{loadError}
        <Button variant="outline" onClick={() => loadArticles(0)} className="ml-3">Erneut versuchen</Button></div>}
      {/* Artikel-Liste */}
      {loadError && articles.length === 0 ? null : articles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Artikel gefunden</h3>
          <p className="text-gray-600 mb-6">
            Du hast noch keine Artikel erstellt.
          </p>
          <Link to="/wizard">
            <Button>
              <PlusIcon className="w-4 h-4 mr-2" />
              Ersten Artikel erstellen
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {groupArticlesByStatus(articles).filter(group => group.articles.length > 0).map(group => (
            <section key={group.id} className="space-y-4" aria-labelledby={`group-${group.id}`}>
              <h2 id={`group-${group.id}`} className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">{group.label} ({group.articles.length})</h2>
              {group.articles.map(article => <ArticleCard key={article.id} article={article} variant="articleList"
                onReschedule={article.status === 'draft' || isEditorOrAdmin ? openRescheduleModal : undefined}
                formatScheduled={formatScheduledAtGerman} />)}
            </section>
          ))}
        </div>
      )}

      {/* Mehr laden Button */}
      {!isLoading && hasMore && (
        <div className="text-center pt-6">
          <Button
            onClick={loadMore}
            disabled={isLoadingMore}
            variant="outline"
            className="flex items-center mx-auto"
          >
            {isLoadingMore ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Wird geladen...</span>
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4 mr-2" />
                Mehr laden
              </>
            )}
          </Button>
        </div>
      )}

      {/* Ende der Liste */}
      {!isLoading && !hasMore && articles.length > 0 && (
        <div className="text-center py-6">
          <p className="text-gray-500 text-sm">
            Alle Artikel wurden geladen ({articles.length} Artikel)
          </p>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleArticle && user && (
        <RescheduleModal
          article={rescheduleArticle}
          currentUser={user}
          onClose={closeRescheduleModal}
          onSuccess={handleRescheduleSuccess}
        />
      )}
    </div>
  );
};
